import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { get } from 'lodash';
import { Card } from 'antd';
import formatPrice from '~helpers/currency';
import SvgFormat from 'common/helpers/svgFormat';
import { formatUppercaseLetters } from '~helpers/change-log';
import { dayFormatType, formatDay } from '~helpers/day';
import EditActualInvoiced from '~pages/dashboard/reconciliation/booking-details/edit-actual-invoiced';
import { decodeBase64 } from 'common/helpers/decodeBase64';

const CommissionDetail = ({ opportunityDetail, handleClickEditInvoice, refreshDetails }) => {
  const { t } = useTranslation();
  const [showEditModal, setShowEditModal] = useState(false);

  const {
    normalCommissionValue,
    bonusCommissionValue,
    totalCommissionValue,
    actualInvoicedValue,
    outstandingClaimValue,
    firstInvoicedAt,
    lastInvoicedAt,
    invoicedNumber,
    calculationType,
    calculationValue,
  } = useMemo(
    () => ({
      calculationType:
        formatUppercaseLetters(
          get(opportunityDetail, 'completedXbooking.commission.calculationType')
            ?.toLowerCase?.()
            ?.split?.('_')
            ?.join?.(' '),
        ) || '-',
      calculationValue: get(opportunityDetail, 'completedXbooking.commission.calculationValue')
        ? formatPrice(
            get(opportunityDetail, 'completedXbooking.commission.calculationValue'),
            get(opportunityDetail, 'completedXbooking.commission.currency'),
          )
        : '-',

      normalCommissionValue: get(opportunityDetail, 'completedXbooking.commission.value')
        ? formatPrice(
            get(opportunityDetail, 'completedXbooking.commission.value'),
            get(opportunityDetail, 'completedXbooking.commission.currency'),
          )
        : '-',
      bonusCommissionValue: get(opportunityDetail, 'completedXbooking.commission.bonusValue')
        ? formatPrice(
            get(opportunityDetail, 'completedXbooking.commission.bonusValue'),
            get(opportunityDetail, 'completedXbooking.commission.currency'),
          )
        : '-',
      totalCommissionValue: get(
        opportunityDetail,
        'completedXbooking.commission.totalCommissionValue',
      )
        ? formatPrice(
            get(opportunityDetail, 'completedXbooking.commission.totalCommissionValue'),
            get(opportunityDetail, 'completedXbooking.commission.currency'),
          )
        : '-',
      actualInvoicedValue: get(
        opportunityDetail,
        'completedXbooking.commission.actualInvoicedValue',
      )
        ? formatPrice(
            get(opportunityDetail, 'completedXbooking.commission.actualInvoicedValue'),
            get(opportunityDetail, 'completedXbooking.commission.currency'),
          )
        : '-',
      outstandingClaimValue: get(
        opportunityDetail,
        'completedXbooking.commission.outstandingClaimValue',
      )
        ? formatPrice(
            get(opportunityDetail, 'completedXbooking.commission.outstandingClaimValue'),
            get(opportunityDetail, 'completedXbooking.commission.currency'),
          )
        : '-',
      firstInvoicedAt: get(opportunityDetail, 'completedXbooking.commission.firstInvoicedAt')
        ? formatDay(
            dayFormatType.UKWITHHM,
            get(opportunityDetail, 'completedXbooking.commission.firstInvoicedAt'),
          )
        : '-',
      lastInvoicedAt: get(opportunityDetail, 'completedXbooking.commission.lastInvoicedAt')
        ? formatDay(
            dayFormatType.UKWITHHM,
            get(opportunityDetail, 'completedXbooking.commission.lastInvoicedAt'),
          )
        : '-',
      invoicedNumber: get(opportunityDetail, 'completedXbooking.commission.invoiceNumber')
        ? get(opportunityDetail, 'completedXbooking.commission.invoiceNumber')
        : '-',
    }),
    [opportunityDetail],
  );

  return (
    <>
      <If condition={showEditModal}>
        <EditActualInvoiced
          activeModal
          onClose={() => {
            setShowEditModal(false);
          }}
          value={get(opportunityDetail, 'completedXbooking.commission.actualInvoicedValue') || ''}
          onConfirm={() => {
            refreshDetails();
          }}
          opportunityDetail={opportunityDetail}
        />
      </If>
      <Card
        title={
          <div className="card-head-title">
            <SvgFormat type="reconciliation-accountBook" className={'reconciliation-accountBook'} />
            {t('cms.reconciliation.booking.details.cards.label.commissionDetails')}
          </div>
        }
        bordered={false}
        className="commission-card"
        headStyle={{
          padding: 0,
          color: 'rgba(0, 0, 0, 0.45)',
          fontSize: '14px',
          borderBottom: 'none',
        }}
        bodyStyle={{
          padding: '0',
          // background: '#F5F5F5',
        }}
        extra={
          <div className="commission-card-id">
            {t('cms.reconciliation.details.comminssion.card_id')}:{' '}
            {get(opportunityDetail, 'completedXbooking.commission.id')
              ? decodeBase64(get(opportunityDetail, 'completedXbooking.commission.id'))?.id || '-'
              : '-'}
          </div>
        }
      >
        <div className="commission-detail">
          <div className="commission-detail__horizontal-container">
            <div className="commission-detail__group-info-card commission-detail__info-card">
              <div className="commission-detail__group-info-card__main">
                <label>{t('cms.reconciliation.list.table.commission_type')}</label>
                <p style={{ fontSize: '16px', lineHeight: '24px' }}>{calculationType}</p>
              </div>
              <div className="commission-detail__group-info-card__main">
                <label>{t('cms.reconciliation.list.table.commission_value')}</label>
                <p style={{ fontSize: '16px', lineHeight: '24px' }}>{calculationValue}</p>
              </div>
              <div className="commission-detail__group-info-card__main">
                <label>
                  {t('cms.reconciliation.booking.details.info.label.Totalcommissionvalue')}
                </label>
                <p style={{ fontSize: '16px', lineHeight: '24px' }}>{totalCommissionValue}</p>
              </div>
              <div className="vertical-divider" />
              <div className="commission-detail__group-info-card__mini">
                <div className="commission-detail__group-info-card__mini__item">
                  <label>
                    {t('cms.reconciliation.booking.details.info.label.Normalommissionvalue')}
                  </label>
                  <p style={{ fontWeight: 400 }}>{normalCommissionValue}</p>
                </div>

                <div className="commission-detail__group-info-card__mini__item">
                  <label>
                    {t('cms.reconciliation.booking.details.info.label.Bonuscommissionvalue')}
                  </label>
                  <p style={{ fontWeight: 400 }}>{bonusCommissionValue}</p>
                </div>
              </div>
            </div>
          </div>

          <div className="commission-detail-box">
            <div className="commission-detail__info-card">
              <label>
                {t('cms.reconciliation.booking.details.info.label.actualInvoicedValue')}
              </label>
              <p className="row" style={{ fontSize: '16px', lineHeight: '24px' }}>
                {actualInvoicedValue}
                <div
                  className="edit-box"
                  onClick={() => {
                    setShowEditModal(true);
                  }}
                >
                  <SvgFormat
                    type="reconciliation-edit-icon"
                    className={'reconciliation-edit-icon'}
                  />
                </div>
              </p>
            </div>

            <div className="commission-detail__info-card">
              <label>
                {t('cms.reconciliation.booking.details.info.label.outstandingClaimValue')}
              </label>
              <p style={{ fontSize: '16px', lineHeight: '24px' }}>{outstandingClaimValue}</p>
            </div>
          </div>

          <div className="commission-detail__footer">
            <div className="commission-detail__footer__content">
              <div className="commission-detail__footer__content__item">
                <label>{t('cms.reconciliation.booking.details.info.label.firstInvoicedAt')}:</label>
                <span>{firstInvoicedAt}</span>
              </div>

              <div className="commission-detail__footer__content__item">
                <label>{t('cms.reconciliation.booking.details.info.label.lastInvoicedAt')}:</label>
                <span>{lastInvoicedAt}</span>
              </div>

              <div className="commission-detail__footer__content__item commission-detail__footer__content__invoice-truncate-container">
                <label className="commission-detail__footer__content__invoice-label">
                  {t('cms.reconciliation.booking.details.info.label.invoicedNumber')}:
                </label>
                <span className="commission-detail__footer__content__invoice-truncate">
                  {invoicedNumber}
                </span>
                <div className="edit-box" onClick={handleClickEditInvoice}>
                  <SvgFormat
                    type="reconciliation-edit-icon"
                    className={'reconciliation-edit-icon'}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </Card>
    </>
  );
};

CommissionDetail.propTypes = {
  opportunityDetail: PropTypes.object,
};

CommissionDetail.defaultProps = {
  opportunityDetail: {},
};

export default CommissionDetail;
